import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty, pickBy } from "lodash";
import pluralize from "pluralize";
import { type DataTableExpandedRows } from "primereact/datatable";
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import {
  Controller,
  type Control,
  type DeepPartial,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
} from "react-hook-form";
import styled from "styled-components";

import { useRootSelector } from "@ds/hooks";

import { getPlayerStatusIndicator } from "@ds/modules/devices/players/components/common/status-indicator";
import { FormationControl } from "@ds/modules/settings/projects/components/forms/controls/formation-control";
import { type FormationControlFormValues } from "@ds/modules/settings/projects/components/forms/controls/types";
import { selectTemplateExperienceFormation } from "@ds/modules/settings/projects/redux/selectors/experience-formation-selectors";
import { selectCurrentUserProjectId } from "@ds/modules/settings/users/redux/selectors";

import {
  DetailsCard,
  EditableDetailsCardDecorator,
  EditableDetailsCardRow,
  EditableDetailsCardRowDecorator,
  type EditableDetailsCardChildProps,
} from "@ds/components/details-view";
import { Button, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { Dropdown, type DropdownChangeEvent } from "@ds/components/forms/inputs";
import { UnassignedLabel } from "@ds/components/forms/labels/common";
import { BaseValidationTitle } from "@ds/components/forms/labels/styles";
import { DetailsTable } from "@ds/components/table/details-table";
import { Column, getReorderColumn } from "@ds/components/table/table-columns";
import { TextLink } from "@ds/components/text-link";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";
import { isFormationPropertyRequired } from "@ds/utils/validation";

import {
  getPlayerIdsWithErrors,
  getPlayerRole,
  getPlayerRoleIssues,
  getPlayerRoleSummary,
  getRoleCounts,
  getRoleOptions,
  getRoleShortfalls,
  getRoleTitle,
  switchPlayerRole,
  type PlayerRoleIssue,
} from "../../utils/player-roles";
import { getPlayerRolesValidationSchema } from "../../utils/validation-schema";
import { ExperiencePlayersControl } from "../forms/controls/experience-players-control";
import { ExperienceDetailsPlayersCardActionsMenu } from "./experience-details-players-card-actions-menu";
import { PlayerFormationValue } from "./player-formation-value";
import { useExperiencePlayersReorder } from "./use-experience-players-reorder";

const EMPTY_PROPERTIES: TemplateFormationFieldPropertyMap = {};

const TableWrapper = styled.div`
  .p-datatable {
    tr > .expander-column {
      width: var(--icon-cell-width) !important;
      text-align: center;
      /* the table truncates every cell with an ellipsis, which shows up next to the toggler here */
      text-overflow: clip !important;
      overflow: visible !important;
    }

    tbody.p-datatable-tbody > tr.p-datatable-row-expansion {
      color: var(--textColor080) !important;
      background: transparent !important;
      box-shadow: none !important;
      outline: none !important;

      > td {
        /* display:table-cell (not block) so the browser honours colSpan for width in native
           table layout. display:block would collapse the td to the first column's width. */
        display: table-cell;
        white-space: normal;
        overflow: visible;
        padding: 4px 16px 12px calc(var(--icon-cell-width) + 16px);
      }
    }
  }
`;

const PlayerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Pill = styled.span<{ $tone?: "neutral" | "warning" }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ $tone }) => ($tone === "warning" ? "var(--warningColor)" : "var(--textColor070)")};
  background: var(--primaryColor005);
  border: 1px solid ${({ $tone }) => ($tone === "warning" ? "var(--warningColor)" : "transparent")};
`;

const Summary = styled.span`
  font-size: 0.85rem;
  color: var(--textColor060);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IssueText = styled.span<{ $severity: PlayerRoleIssue["severity"] }>`
  font-size: 0.85rem;
  color: ${({ $severity }) => ($severity === "error" ? "var(--errorColor)" : "var(--warningColor)")};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PlayerDetailsRowWrapper = styled.div`
  display: flex;
  /* line the label up with the value's first line, not the middle of the value and a warning below it */
  align-items: baseline;
  min-height: 40px;
  padding: 4px 0;
  line-height: 1.3;
`;

const PlayerDetailsRowLabel = styled.div`
  min-width: 26%;
  color: var(--textColor060);
  font-weight: 600;
`;

const PlayerDetailsRowValue = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  flex-grow: 1;
  padding-left: 5px;
`;

/**
 * A compact label/value row for a player's role values. DetailsCardRow can't hold these inputs: its read-only layout
 * prevents mousedown to support click-to-edit, which also keeps text inputs from taking focus.
 */
const PlayerDetailsRow: FC<{ label: string; isRequired?: boolean; children: ReactNode }> = ({
  label,
  isRequired = false,
  children,
}) => (
  <PlayerDetailsRowWrapper>
    <PlayerDetailsRowLabel>
      <BaseValidationTitle $isRequired={isRequired}>{label}</BaseValidationTitle>
    </PlayerDetailsRowLabel>
    <PlayerDetailsRowValue>{children}</PlayerDetailsRowValue>
  </PlayerDetailsRowWrapper>
);

const FieldInput = styled.div`
  width: 400px;
  max-width: 100%;

  .p-inputtext,
  .p-dropdown,
  .p-autocomplete > .p-autocomplete-input {
    width: 100%;
  }
`;

const FieldIssue = styled(IssueText)`
  display: block;
  margin-top: 4px;
`;

type PlayerRolesFormProps = EditableDetailsCardChildProps<ExperienceFormData>;

const devicePath = (playerId: number) => `experience.formation.devices.${playerId}` as const;

const sortIssues = (issues: PlayerRoleIssue[] = []) =>
  [...issues].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1));

const PlayerRoleDetails: FC<{
  player: Player;
  roles: FormationRoleMap;
  properties: TemplateFormationFieldPropertyMap;
  devices?: ExperienceDeviceFormationMap;
  playerIds: number[];
  issues: PlayerRoleIssue[];
  cardProps: PlayerRolesFormProps;
}> = ({ player, roles, properties, devices, playerIds, issues, cardProps }) => {
  const { isEditing, control, setValue } = cardProps;
  const roleKey = getPlayerRole(devices, player.id);
  const role = roleKey ? roles[roleKey] : undefined;
  const fieldIssues = (field: string) =>
    issues
      .filter(issue => issue.field === field)
      .map(issue => (
        <FieldIssue key={`${field}-${issue.message}`} $severity={issue.severity}>
          {issue.message}
        </FieldIssue>
      ));

  const onRoleChangeHandler = useCallback(
    (e: DropdownChangeEvent) =>
      setValue(devicePath(player.id), switchPlayerRole(roles, devices?.[player.id], e.value as string), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }),
    [setValue, player.id, roles, devices],
  );

  return (
    <div id={`player-roles-row-${player.id}`}>
      <PlayerDetailsRow label="Role">
        {isEditing ? (
          <FieldInput>
            <Controller
              name={`${devicePath(player.id)}.role`}
              control={control}
              render={({ field: { ref, value } }) => (
                <Dropdown
                  inputRef={ref}
                  value={value}
                  options={getRoleOptions(roles, devices, playerIds, player.id)}
                  optionLabel="label"
                  optionValue="value"
                  optionDisabled="disabled"
                  placeholder="Pick a role"
                  ariaLabel={`Role of ${player.name}`}
                  onChange={onRoleChangeHandler}
                />
              )}
            />
          </FieldInput>
        ) : (
          roleKey && getRoleTitle(roles, roleKey)
        )}
        {fieldIssues("role")}
      </PlayerDetailsRow>

      {(role?.fields || [])
        .filter(field => !!properties[field])
        .map(field => {
          const property = properties[field];

          return (
            <PlayerDetailsRow
              key={field}
              label={property.title}
              isRequired={isFormationPropertyRequired(property.metadata?.validation_rules)}
            >
              {isEditing ? (
                <FieldInput>
                  <FormationControl
                    name={`${devicePath(player.id)}.properties.${field}`}
                    control={control as unknown as Control<FormationControlFormValues>}
                    setValue={setValue as unknown as UseFormSetValue<FormationControlFormValues>}
                    property={property}
                    excludeLabel
                  />
                </FieldInput>
              ) : (
                <PlayerFormationValue property={property} value={devices?.[player.id]?.properties?.[field]?.value} />
              )}
              {fieldIssues(field)}
            </PlayerDetailsRow>
          );
        })}
    </div>
  );
};

const PlayerRolesCardContent: FC<{
  cardProps: PlayerRolesFormProps;
  experience: Experience;
  roles: FormationRoleMap;
  properties: TemplateFormationFieldPropertyMap;
  reorder: ReturnType<typeof useExperiencePlayersReorder>;
}> = ({ cardProps, experience, roles, properties, reorder }) => {
  const { tablePlayers, isReordering, onSaveReorderHandler, onCancelReorderHandler, onTableRowReorderHandler } =
    reorder;

  const { isEditing } = cardProps;
  const formPlayers = useMemo(() => (cardProps.entity.players || []) as Player[], [cardProps.entity.players]);
  const playerIds = useMemo(() => formPlayers.map(({ id }) => id), [formPlayers]);
  const devices = cardProps.entity.experience?.formation?.devices;

  const issues = getPlayerRoleIssues({ roles, properties, devices, playerIds });
  const shortfalls = getRoleShortfalls({ roles, devices, playerIds });
  const issuesCount = Object.values(issues).flat().length + shortfalls.length;
  const roleCounts = getRoleCounts(devices, playerIds);

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const areAllExpanded = playerIds.length > 0 && playerIds.every(playerId => expandedRows[playerId]);
  const expandPlayers = useCallback(
    (ids: number[]) => setExpandedRows(rows => ({ ...rows, ...Object.fromEntries(ids.map(id => [id, true])) })),
    [],
  );

  // Editing opens every player unless some are already open, in which case those stay as they are.
  // Players added while editing always open, since they need a role.
  const playersSeenWhileEditing = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (!isEditing) {
      playersSeenWhileEditing.current = null;
      return;
    }

    if (!playersSeenWhileEditing.current) {
      playersSeenWhileEditing.current = new Set(playerIds);
      setExpandedRows(rows =>
        playerIds.some(playerId => rows[playerId])
          ? rows
          : { ...rows, ...Object.fromEntries(playerIds.map(playerId => [playerId, true])) },
      );
      return;
    }

    const seenPlayerIds = playersSeenWhileEditing.current;
    const addedPlayerIds = playerIds.filter(playerId => !seenPlayerIds.has(playerId));
    if (addedPlayerIds.length) {
      addedPlayerIds.forEach(playerId => seenPlayerIds.add(playerId));
      expandPlayers(addedPlayerIds);
    }
  }, [isEditing, playerIds, expandPlayers]);

  const onToggleAllHandler = useCallback(
    () => (areAllExpanded ? setExpandedRows({}) : expandPlayers(playerIds)),
    [areAllExpanded, expandPlayers, playerIds],
  );

  const onInvalidHandler = useCallback(
    (errors: FieldErrors<ExperienceFormData>) => {
      const invalidPlayerIds = getPlayerIdsWithErrors(errors);
      const firstInvalidPlayerId = playerIds.find(playerId => invalidPlayerIds.includes(playerId));
      if (!firstInvalidPlayerId) {
        // the error isn't about one player: open them all so it can't go unnoticed
        expandPlayers(playerIds);
      } else {
        expandPlayers([firstInvalidPlayerId]);
        setTimeout(
          () =>
            document
              .getElementById(`player-roles-row-${firstInvalidPlayerId}`)
              ?.scrollIntoView({ block: "center", behavior: "smooth" }),
          0,
        );
      }
    },
    [playerIds, expandPlayers],
  );

  const onSaveHandler = useCallback(
    () =>
      cardProps.handleSubmitAllFields(data => {
        cardProps.onSubmitForm(data);
        cardProps.onSaveAllFields();
      }, onInvalidHandler)(),
    [cardProps, onInvalidHandler],
  );

  const renderPlayerHeader = (player: Player, rowIndex: number) => {
    const roleKey = getPlayerRole(devices, player.id);
    const [topIssue] = sortIssues(issues[player.id]);
    const summary = getPlayerRoleSummary(roles, properties, devices?.[player.id]);
    const isNew = !experience.device_ids.includes(player.id);

    return (
      <PlayerHeader>
        <span className="text-sm text-gray-500">{rowIndex + 1}.</span>
        <TextLink to={composeDetailsRouteByEntityType(player)}>{player.name}</TextLink>
        {roleKey && <Pill>{getRoleTitle(roles, roleKey)}</Pill>}
        {topIssue ? (
          <IssueText $severity={topIssue.severity}>
            <i className="pi pi-exclamation-triangle mr-1" aria-hidden="true" />
            {topIssue.message}
          </IssueText>
        ) : (
          !!summary && <Summary>{summary}</Summary>
        )}
        {isNew && <Summary>new, not saved yet</Summary>}
      </PlayerHeader>
    );
  };

  return (
    <DetailsCard
      title="Players"
      icon="desktop"
      actions={
        <HeaderActions>
          {Object.entries(roles).map(([roleKey, role]) => (
            <Pill key={`role-count-${roleKey}`}>
              {role.title} {roleCounts[roleKey] || 0}
              {role.max !== undefined && ` of ${role.max}`}
            </Pill>
          ))}
          {issuesCount > 0 && (
            <Pill $tone="warning" title={shortfalls.join("\n") || undefined}>
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
              {pluralize("issue", issuesCount, true)}
            </Pill>
          )}
          {playerIds.length > 0 && (
            <Button
              label={areAllExpanded ? "Collapse all" : "Expand all"}
              ariaLabel={areAllExpanded ? "collapse all players" : "expand all players"}
              buttonType={ButtonType.Round}
              icon={areAllExpanded ? IconType.CollapseAll : IconType.ExpandAll}
              onClick={onToggleAllHandler}
            />
          )}
          <ExperienceDetailsPlayersCardActionsMenu
            mode={isReordering ? "REORDERING" : isEditing ? "EDITING" : "DEFAULT"}
            isDirty={cardProps.isDirty}
            isAggregated={false}
            saveLabel="Save players"
            onEdit={() => {
              cardProps.onEditRow("players")();
            }}
            onReset={cardProps.onCancelEdit}
            onSubmit={onSaveHandler}
            onSaveReorder={onSaveReorderHandler}
            onCancelReorder={onCancelReorderHandler}
          />
        </HeaderActions>
      }
    >
      {isEditing && (
        <EditableDetailsCardRowDecorator
          field="players"
          input={<ExperiencePlayersControl control={cardProps.control} autoFocus excludeLabel />}
          shouldEditWholeCard
          {...cardProps}
        >
          {rowProps => <EditableDetailsCardRow label="Choose players" editableSettings={rowProps} />}
        </EditableDetailsCardRowDecorator>
      )}

      {/* the table state is shared by every experience's page and isn't cleared for one without players */}
      {formPlayers.length ? (
        <TableWrapper>
          <DetailsTable
            value={isEditing ? formPlayers : tablePlayers}
            dataKey="id"
            className="experience-players-table"
            expandedRows={expandedRows}
            onRowToggle={e => setExpandedRows(e.data as DataTableExpandedRows)}
            rowExpansionTemplate={(player: Player) => (
              <PlayerRoleDetails
                player={player}
                roles={roles}
                properties={properties}
                devices={devices}
                playerIds={playerIds}
                issues={issues[player.id] || []}
                cardProps={cardProps}
              />
            )}
            onRowReorder={onTableRowReorderHandler}
          >
            {!isEditing && getReorderColumn({ rowReorder: true })}
            <Column expander className="expander-column" resizeable={false} />
            <Column
              header="Player Name"
              body={(player: Player, props) => renderPlayerHeader(player, props.rowIndex)}
              resizeable={false}
            />
            <Column
              header="Status"
              body={(player: Player) => getPlayerStatusIndicator(player)}
              className="w150"
              resizeable={false}
            />
          </DetailsTable>
        </TableWrapper>
      ) : (
        !isEditing && <UnassignedLabel text="Experience has no players" />
      )}
    </DetailsCard>
  );
};

/**
 * Players card for a project whose formation assigns roles: each player row expands to its role and its own values
 * of the role's fields. Edit players opens every row for editing, and Save players saves players, roles and
 * values in one request.
 */
export const ExperienceDetailsPlayerRolesCard: FC<{
  experience: Experience;
  roles: FormationRoleMap;
  onSubmit: (changedFields: DeepPartial<Experience>) => void;
}> = ({ experience, roles, onSubmit }) => {
  const projectId = useRootSelector(selectCurrentUserProjectId);
  const properties =
    useRootSelector(state => selectTemplateExperienceFormation(state, projectId))?.properties || EMPTY_PROPERTIES;
  const reorder = useExperiencePlayersReorder(experience);

  const validationResolver = useMemo(
    () => yupResolver(getPlayerRolesValidationSchema(roles, properties)),
    [roles, properties],
  );

  const resolveOnSubmit = useCallback(
    (getValues: UseFormGetValues<ExperienceFormData>) =>
      ({ players, experience: changedExperience }: DeepPartial<ExperienceFormData>) => {
        const currentPlayerIds = (getValues("players") || []).map(({ id }) => id);
        // a player removed in the same edit takes its role assignment with it on the server
        const changedDevices = pickBy(changedExperience?.formation?.devices || {}, (_, playerId) =>
          currentPlayerIds.includes(Number(playerId)),
        );

        const changedFields: DeepPartial<Experience> = {};
        if (players) {
          changedFields.device_ids = currentPlayerIds;
        }

        if (!isEmpty(changedDevices)) {
          changedFields.formation = { devices: changedDevices };
        }

        if (!isEmpty(changedFields)) {
          onSubmit(changedFields);
        }
      },
    [onSubmit],
  );

  // Player IDs are numeric keys, so without an existing object react-hook-form would build `devices` as an array
  const initialValue = useMemo(
    () => ({
      experience: {
        ...experience,
        formation: { ...experience.formation, devices: experience.formation?.devices || {} },
      },
      players: reorder.aggregatedExperiencePlayers,
    }),
    [experience, reorder.aggregatedExperiencePlayers],
  );

  return (
    <EditableDetailsCardDecorator<ExperienceFormData>
      initialValue={initialValue}
      validationResolver={validationResolver}
      resolveOnSubmit={resolveOnSubmit}
      shouldKeepUnsavedEdits
    >
      {cardProps => (
        <PlayerRolesCardContent
          cardProps={cardProps}
          experience={experience}
          roles={roles}
          properties={properties}
          reorder={reorder}
        />
      )}
    </EditableDetailsCardDecorator>
  );
};
